<template>
	<div class="stop-list-widget border rounded-plus p-3 mr-1">
		<div class="d-flex justify-content-center mb-3 mt-3 pt-3 pb-3" v-if="is_load">
			<b-spinner variant="success" label="Loading..."></b-spinner>
		</div>

		<div class="chart" v-else>
			<div class="stat-grid mb-4">
				<div class="stat-card" v-for="stat in statCards" :key="stat.key">
					<div class="stat-card__icon" :class="'bg-' + stat.color + '-50'">
						<i class="fal" :class="[stat.icon, 'color-' + stat.color + '-500']"></i>
					</div>
					<div class="stat-card__body">
						<span class="stat-card__label">{{ stat.label }}</span>
						<span class="stat-card__value" :class="'color-' + stat.color + '-500'">{{ stat.value }}</span>
					</div>
				</div>
			</div>

			<div class="chart-card">
				<highcharts :options="chartOptions" :highcharts="char3d"></highcharts>
			</div>

			<div class="mt-4" v-if="summary.total_in_stop > 0">
				<div class="d-flex align-items-center mb-2">
					<i class="fal fa-history mr-2 opacity-50"></i>
					<span class="fs-sm fw-500 opacity-75">
						{{ __t('STOP_LIST_LONGEST') }}
					</span>
				</div>

				<div class="table-responsive" v-if="longest_items.length > 0">
					<table class="table table-sm stop-list-table mb-0">
						<thead>
							<tr class="fs-xs opacity-50">
								<th class="rank-col"></th>
								<th>{{ __t('PRODUCT') }}</th>
								<th>{{ __t('RESTAURANT') }}</th>
								<th class="text-right">{{ __t('STOP_LIST_DURATION_SHORT') }}</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="(row, idx) in longest_items" :key="'item_' + row.id">
								<td class="rank-col">
									<span class="rank-badge">{{ idx + 1 }}</span>
								</td>
								<td class="text-truncate" style="max-width: 180px;">{{ row.title }}</td>
								<td class="text-truncate opacity-75" style="max-width: 140px;">{{ row.restaurant.title }}</td>
								<td class="text-right">
									<span class="badge rounded-pill bg-danger-50 color-danger-500 duration-badge">
										{{ row.duration_human }}
									</span>
								</td>
							</tr>
						</tbody>
					</table>
				</div>

				<div class="fs-sm opacity-50" v-else>
					{{ __t('NO_DATA') }}
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import {Chart} from 'highcharts-vue'
import Highcharts from 'highcharts'

export default {
	name: "stop_list_duration",

	components: {
		highcharts: Chart
	},

	created() {
		EventBus.$on('change_date', res => {
			this.getStat();
		});
	},

	mounted() {
		this.getStat();
	},

	data() {
		return {
			is_load: true,
			char3d: Highcharts,

			summary: {
				total_in_stop: 0,
				avg_duration_human: '',
				min_duration_human: '',
				max_duration_human: '',
			},

			longest_items: [],

			chartOptions: {
				chart: {
					type: 'column',
					style: {
						fontFamily: 'inherit'
					}
				},
				title: {
					text: this.__t('STOP_LIST_BY_RESTAURANT'),
					align: 'left',
					style: {
						fontSize: '15px',
						fontWeight: '600',
						color: '#3b3f5c'
					}
				},
				credits: {
					enabled: false
				},
				xAxis: {
					categories: [],
					crosshair: true,
					lineColor: '#eef0f5',
					labels: {
						style: {fontSize: '11px'}
					}
				},
				yAxis: [{
					title: {
						text: this.__t('STOP_LIST_COUNT')
					},
					allowDecimals: false,
					gridLineColor: '#f1f2f7'
				}, {
					title: {
						text: this.__t('STOP_LIST_AVG_DURATION_H')
					},
					opposite: true,
					allowDecimals: false,
					gridLineWidth: 0
				}],
				legend: {
					enabled: true,
					itemStyle: {
						fontWeight: '500'
					}
				},
				tooltip: {
					shared: true
				},
				plotOptions: {
					column: {
						borderRadius: 6,
						pointPadding: 0.15,
						groupPadding: 0.12,
						borderWidth: 0
					},
					spline: {
						lineWidth: 3,
						marker: {
							radius: 4,
							lineWidth: 2,
							lineColor: '#ffffff'
						}
					}
				},
				series: [{
					name: this.__t('STOP_LIST_COUNT'),
					type: 'column',
					yAxis: 0,
					color: '#ee3158',
					data: [],
					tooltip: {
						valueSuffix: ' ' + this.__t('STOP_LIST_ITEMS')
					}
				}, {
					name: this.__t('STOP_LIST_AVG_DURATION'),
					type: 'spline',
					yAxis: 1,
					color: '#1dc9b7',
					data: [],
					tooltip: {
						valueSuffix: ' ' + this.__t('STOP_LIST_HOURS')
					}
				}]
			}
		}
	},

	methods: {
		async getStat() {
			this.is_load = true;

			await Promise.all([
				this.loadSummary(),
				this.loadLongestItems(),
			]).finally(() => {
				this.is_load = false;
			});
		},

		async loadSummary() {
			await api.post('/internal/v1/catalog/dashboard/stop-list/summary').then(({data}) => {
				if (data.status == "operation_success") {
					let res = data.data;

					this.summary.total_in_stop = res.total_in_stop || 0;
					this.summary.avg_duration_human = res.avg_duration_human || '-';
					this.summary.min_duration_human = res.min_duration_human || '-';
					this.summary.max_duration_human = res.max_duration_human || '-';

					let by_restaurant = res.by_restaurant || [];

					this.chartOptions.xAxis.categories = by_restaurant.map(item => item.restaurant_title);
					this.chartOptions.series[0].data = by_restaurant.map(item => item.count);
					this.chartOptions.series[1].data = by_restaurant.map(item => {
						return Math.round((item.avg_duration_sec / 3600) * 10) / 10;
					});
				}
			}).catch(err => {
				EventBus.$emit('show_toast', {err: err.message, type: "danger", title: err.name})
			});
		},

		async loadLongestItems() {
			await api.post('/internal/v1/catalog/dashboard/stop-list/list', {
				list: {
					limit: 5,
					ordering: 'added',
					direction: 'ASC',
				}
			}).then(({data}) => {
				if (data.status == "operation_success") {
					this.longest_items = data.data || [];
				}
			}).catch(err => {
				EventBus.$emit('show_toast', {err: err.message, type: "danger", title: err.name})
			});
		}
	},

	computed: {
		dash_param_date() {
			return this.$store.state.dashboard.main.date;
		},

		statCards() {
			return [
				{
					key: 'total',
					icon: 'fa-ban',
					color: 'danger',
					label: this.__t('STOP_LIST_TOTAL'),
					value: this.summary.total_in_stop
				},
				{
					key: 'avg',
					icon: 'fa-hourglass-half',
					color: 'warning',
					label: this.__t('STOP_LIST_AVG_DURATION'),
					value: this.summary.avg_duration_human
				},
				{
					key: 'min',
					icon: 'fa-bolt',
					color: 'success',
					label: this.__t('STOP_LIST_MIN_DURATION'),
					value: this.summary.min_duration_human
				},
				{
					key: 'max',
					icon: 'fa-history',
					color: 'danger',
					label: this.__t('STOP_LIST_MAX_DURATION'),
					value: this.summary.max_duration_human
				}
			];
		}
	},
}
</script>

<style scoped>
.stop-list-widget {
	background: #fff;
}

.stat-grid {
	display: grid;
	grid-template-columns: repeat(4, 1fr);
	gap: 12px;
}

@media (max-width: 767px) {
	.stat-grid {
		grid-template-columns: repeat(2, 1fr);
	}
}

.stat-card {
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 14px;
	border: 1px solid #eef0f5;
	border-radius: 12px;
	transition: box-shadow .15s ease, transform .15s ease;
}

.stat-card:hover {
	box-shadow: 0 4px 14px rgba(20, 20, 40, .06);
	transform: translateY(-1px);
}

.stat-card__icon {
	flex: 0 0 40px;
	width: 40px;
	height: 40px;
	border-radius: 10px;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 16px;
}

.stat-card__body {
	display: flex;
	flex-direction: column;
	min-width: 0;
}

.stat-card__label {
	font-size: 11px;
	font-weight: 300;
	opacity: .6;
	line-height: 1.3;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.stat-card__value {
	font-size: 1.15rem;
	font-weight: 600;
	line-height: 1.4;
}

.chart-card {
	border: 1px solid #eef0f5;
	border-radius: 12px;
	padding: 12px 8px 4px;
}

.stop-list-table thead th {
	border-top: none;
	border-bottom: 1px solid #eef0f5;
	font-weight: 500;
	text-transform: uppercase;
	letter-spacing: .02em;
}

.stop-list-table tbody tr {
	transition: background-color .15s ease;
}

.stop-list-table tbody tr:hover {
	background-color: #fbfbfd;
}

.stop-list-table td {
	vertical-align: middle;
}

.rank-col {
	width: 28px;
}

.rank-badge {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 20px;
	height: 20px;
	border-radius: 50%;
	background: #f1f2f7;
	font-size: 10px;
	font-weight: 600;
	opacity: .7;
}

.duration-badge {
	font-weight: 600;
	font-size: .8rem;
	padding: .35em .7em;
}
</style>
